// As horas dos lembretes (passo 18, PR 2) — SEMPRE em America/Sao_Paulo, por `Intl`, nunca pelo fuso do aparelho.
//
// Puras: recebem o instante `agora` e devolvem instantes e textos. Nenhuma usa `getHours()`/`getDate()` (fuso do
// aparelho) nem `getUTC*` para o dia e a hora DE BRASÍLIA — quem transforma instante em dia e hora é o `Intl` com o
// fuso fixo. A volta (dia + hora de Brasília → instante) é `instanteEmBrasilia`, em duas passadas: vale também se
// o horário de verão voltar. (`somarDias` usa `Date.UTC` só como calendário, sem fuso nenhum.)
import { ehDataIso } from "@/lib/dataNoFuso";

export const FUSO_DE_BRASILIA = "America/Sao_Paulo";
export const MSG_HORA_PASSADA = "Essa hora já passou. Escolha um horário à frente.";
export const MSG_ATE_UM_ANO = "Escolha uma data em até um ano.";

const MINUTO = 60_000;
/** O teto do servidor (366 dias): a tela recusa antes, com a mesma frase. */
const TETO_MS = 366 * 24 * 60 * MINUTO;

export interface PartesEmBrasilia {
  ano: number;
  mes: number;
  dia: number;
  hora: number;
  minuto: number;
  /** 0 = domingo … 6 = sábado. */
  diaDaSemana: number;
}

const PARTES = new Intl.DateTimeFormat("en-US", {
  timeZone: FUSO_DE_BRASILIA,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
  weekday: "short",
});
const DIA_DA_SEMANA: Record<string, number> = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
};

export function partesEmBrasilia(instante: Date): PartesEmBrasilia {
  const p: Record<string, string> = {};
  for (const { type, value } of PARTES.formatToParts(instante)) p[type] = value;
  return {
    ano: Number(p.year),
    mes: Number(p.month),
    dia: Number(p.day),
    // Alguns motores escrevem meia-noite como "24" mesmo com h23.
    hora: Number(p.hour) % 24,
    minuto: Number(p.minute),
    diaDaSemana: DIA_DA_SEMANA[p.weekday] ?? 0,
  };
}

const dois = (n: number) => String(n).padStart(2, "0");

/** `AAAA-MM-DD` do dia em Brasília. */
export function dataEmBrasilia(instante: Date): string {
  const { ano, mes, dia } = partesEmBrasilia(instante);
  return `${String(ano).padStart(4, "0")}-${dois(mes)}-${dois(dia)}`;
}

/** Soma dias a `AAAA-MM-DD` (calendário puro). */
export function somarDias(data: string, dias: number): string {
  const [a, m, d] = data.split("-").map(Number);
  const t = new Date(Date.UTC(a, m - 1, d + dias));
  return `${t.getUTCFullYear()}-${dois(t.getUTCMonth() + 1)}-${dois(t.getUTCDate())}`;
}

/** O instante de `data` (`AAAA-MM-DD`) às `hora` (`HH:mm`) em Brasília. */
export function instanteEmBrasilia(data: string, hora: string): Date {
  const [a, m, d] = data.split("-").map(Number);
  const [h, mi] = hora.split(":").map(Number);
  const desejado = Date.UTC(a, m - 1, d, h, mi);
  // 1ª passada supondo UTC-3 (o fuso de hoje); a 2ª corrige pela diferença que o Intl mostra.
  let palpite = desejado + 3 * 60 * MINUTO;
  for (let i = 0; i < 2; i++) {
    const p = partesEmBrasilia(new Date(palpite));
    palpite += desejado - Date.UTC(p.ano, p.mes - 1, p.dia, p.hora, p.minuto);
  }
  return new Date(palpite);
}

const SEMANA_CURTA = new Intl.DateTimeFormat("pt-BR", {
  timeZone: FUSO_DE_BRASILIA,
  weekday: "short",
});
const POR_EXTENSO = new Intl.DateTimeFormat("pt-BR", {
  timeZone: FUSO_DE_BRASILIA,
  weekday: "long",
  day: "numeric",
  month: "long",
});

/** "09:00" */
export function horaCurta(instante: Date): string {
  const { hora, minuto } = partesEmBrasilia(instante);
  return `${dois(hora)}:${dois(minuto)}`;
}

/** "qui 09/10" — o dia da semana em pt-BR sem o ponto ("qui", não "qui."). */
export function diaCurto(instante: Date): string {
  const { dia, mes } = partesEmBrasilia(instante);
  return `${SEMANA_CURTA.format(instante).replace(".", "").toLowerCase()} ${dois(dia)}/${dois(mes)}`;
}

function valido(iso: string): Date | null {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** "07/10 14:32" em Brasília — a data da mensagem de origem na linha "Guardado de…" (passo 18, PR 3). Inválida: "". */
export function diaMesEHora(iso: string): string {
  const d = valido(iso);
  if (!d) return "";
  const { dia, mes, hora, minuto } = partesEmBrasilia(d);
  return `${dois(dia)}/${dois(mes)} ${dois(hora)}:${dois(minuto)}`;
}

/** "hoje · 15:32" no mesmo dia de Brasília; senão "qui 09/10 · 09:00". Sem "em 2 h": a bolha não se redesenha. */
export function rotuloDaHora(iso: string, agora: Date): string {
  const d = valido(iso);
  if (!d) return "";
  const dia = dataEmBrasilia(d) === dataEmBrasilia(agora) ? "hoje" : diaCurto(d);
  return `${dia} · ${horaCurta(d)}`;
}

/** "hoje às 15:32" / "qui 09/10 às 09:00" — o texto dos avisos de resultado. */
export function quandoPorExtenso(iso: string, agora: Date): string {
  const d = valido(iso);
  if (!d) return "";
  const dia = dataEmBrasilia(d) === dataEmBrasilia(agora) ? "hoje" : diaCurto(d);
  return `${dia} às ${horaCurta(d)}`;
}

/** "quinta, 9 de outubro às 09:00" — para o leitor de tela. */
export function paraOLeitor(iso: string): string {
  const d = valido(iso);
  if (!d) return "";
  return `${POR_EXTENSO.format(d).replace("-feira", "")} às ${horaCurta(d)}`;
}

export type ChaveDoAtalho = "uma-hora" | "hoje-18" | "amanha-9" | "segunda-9";

export interface AtalhoDeLembrete {
  chave: ChaveDoAtalho;
  rotulo: string;
  instante: Date;
  /** O texto à direita do atalho ("hoje · 15:32", "sex 09/10 · 09:00"). */
  direita: string;
}

/** Os atalhos do seletor (desenho §5.2), calculados em Brasília. */
export function atalhosDeLembrete(agora: Date): AtalhoDeLembrete[] {
  const hoje = dataEmBrasilia(agora);
  const { hora, minuto, diaDaSemana } = partesEmBrasilia(agora);
  const lista: AtalhoDeLembrete[] = [];

  // Daqui a 1 hora: o instante + 60 min, segundos zerados.
  const umaHora = new Date(Math.floor((agora.getTime() + 60 * MINUTO) / MINUTO) * MINUTO);
  lista.push({
    chave: "uma-hora",
    rotulo: "Daqui a 1 hora",
    instante: umaHora,
    direita: `${dataEmBrasilia(umaHora) === hoje ? "hoje" : "amanhã"} · ${horaCurta(umaHora)}`,
  });

  // Hoje às 18:00: só quando faltam pelo menos 15 min (até 17:45).
  if (hora * 60 + minuto <= 17 * 60 + 45) {
    lista.push({
      chave: "hoje-18",
      rotulo: "Hoje às 18:00",
      instante: instanteEmBrasilia(hoje, "18:00"),
      direita: "hoje · 18:00",
    });
  }

  const amanha9 = instanteEmBrasilia(somarDias(hoje, 1), "09:00");
  lista.push({
    chave: "amanha-9",
    rotulo: "Amanhã às 9:00",
    instante: amanha9,
    direita: `${diaCurto(amanha9)} · 09:00`,
  });

  // Segunda às 9:00: a próxima segunda (hoje segunda → a da semana que vem); some quando a segunda é amanhã.
  const ateSegunda = (1 - diaDaSemana + 7) % 7 || 7;
  if (ateSegunda !== 1) {
    const segunda9 = instanteEmBrasilia(somarDias(hoje, ateSegunda), "09:00");
    lista.push({
      chave: "segunda-9",
      rotulo: "Segunda às 9:00",
      instante: segunda9,
      direita: `${diaCurto(segunda9)} · 09:00`,
    });
  }
  return lista;
}

/**
 * O "Escolher data e hora…": `null` = campo vazio ou data inválida (nada a confirmar); `{erro}` = a frase da tela;
 * `{instante}` = pronto para o PUT. As mesmas regras do servidor (depois de agora, até 366 dias).
 */
export function escolhaDoLembrete(
  data: string,
  hora: string,
  agora: Date
): { instante: Date } | { erro: string } | null {
  if (!ehDataIso(data) || !/^\d{2}:\d{2}$/.test(hora)) return null;
  const instante = instanteEmBrasilia(data, hora);
  if (Number.isNaN(instante.getTime())) return null;
  if (instante.getTime() <= agora.getTime()) return { erro: MSG_HORA_PASSADA };
  if (instante.getTime() > agora.getTime() + TETO_MS) return { erro: MSG_ATE_UM_ANO };
  return { instante };
}
